import { client } from '../util/sdk';
import { FSPS } from '../consts';
import { POST, DELETE } from '../util/api';

const loadURL = (fspId) => `/v2/${FSPS}/${fspId}/loans/search`;
const historyURL = (fspId) => `/v2/${FSPS}/${fspId}/loans/histtrans`;
const scheduledURL = (fspId) => `/v2/${FSPS}/${fspId}/loans/schedtrans`;
const deletePalPaymentURL = (fspId, payerId, loanSchedId) =>
  `/v2/fsps/${fspId}/payers/${payerId}/palpayments/${loanSchedId}`;

const TRANSACTION_REQUEST_FIELDS = ['loanNumber', 'offset', 'limit', 'order'];
const transactionRequestData = (params = {}) =>
  TRANSACTION_REQUEST_FIELDS.reduce((data, key) => {
    if (params[key] !== undefined) {
      data[key] = params[key];
    }
    return data;
  }, {});

const loadTransactions = async (
  { fspId, ...params },
  auth,
  getURL,
  authError,
  responseError
) => {
  if (!auth || !auth.id) {
    throw new Error(authError);
  }
  const queryParams = { adminUserId: auth.id };
  const data = transactionRequestData(params);
  const result = await client.executeRequest(POST, getURL(fspId), {
    queryParams,
    data,
  });
  if (result && typeof result === 'object' && result.error) {
    throw new Error(result.error.message || responseError);
  }
  return result;
};

export const loadByLoanNumber = async ({ fspId, loanNumber }, auth) => {
  if (!auth || !auth.id) {
    throw new Error(
      'auth or auth.id is undefined in guestLoanSearch loadByLoanNumber'
    );
  }
  const queryParams = { adminUserId: auth.id };
  const payload = {
    ownerType: 'guest',
    loanNumber:
      typeof loanNumber === 'object' && loanNumber.value !== undefined
        ? loanNumber
        : { value: loanNumber },
  };
  const result = await client.executeRequest(POST, loadURL(fspId), {
    queryParams,
    data: payload,
  });
  if (result && typeof result === 'object' && result.error) {
    throw new Error(result.error.message || 'Error loading loans by number');
  }
  return result?.payload?.loans ?? result?.loans ?? result;
};

// Search by email only (with optional limit/offset)
export const loadByEmail = async ({ fspId, email, limit, offset }, auth) => {
  if (!auth || !auth.id) {
    throw new Error(
      'auth or auth.id is undefined in guestLoanSearch loadByEmail'
    );
  }
  // Ensure email is always wrapped as { value: ... }
  const payload = {
    ownerType: 'guest',
    email:
      typeof email === 'object' && email.value !== undefined
        ? email
        : { value: email },
  };
  const queryParams = { adminUserId: auth.id };
  if (offset !== undefined) queryParams.offset = offset;
  if (limit !== undefined) queryParams.limit = limit;
  return client.executeRequest(POST, loadURL(fspId), {
    queryParams,
    data: payload,
  });
};

export const loadHistoryTransactions = async (params, auth) =>
  loadTransactions(
    params,
    auth,
    historyURL,
    'auth or auth.id is undefined in guestLoanSearch loadHistoryTransactions',
    'Error loading history transactions'
  );

export const loadScheduledTransactions = async (params, auth) =>
  loadTransactions(
    params,
    auth,
    scheduledURL,
    'auth or auth.id is undefined in guestLoanSearch loadScheduledTransactions',
    'Error loading scheduled transactions'
  );

export const cancelGuestScheduledPayment = async (
  { fspId, payerId, loanSchedId },
  userId
) => {
  if (!userId) {
    throw new Error(
      'userId is undefined in guestLoanSearch cancelGuestScheduledPayment'
    );
  }
  // Only include required query parameter for DELETE, and do NOT send a body
  const queryParams = {
    userId,
  };
  const result = await client.executeRequest(
    DELETE,
    deletePalPaymentURL(fspId, payerId, loanSchedId),
    {
      queryParams,
      returnModel: { name: 'palpayment' },
    }
  );
  if (result && typeof result === 'object' && result.error) {
    throw new Error(
      result.error.message || 'Error canceling scheduled payment'
    );
  }
  return result;
};
